import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PatientInfoForm from './components/PatientInfoForm';
import DateSelector from './components/DateSelector';
import TimeSlotGrid from './components/TimeSlotGrid';
import BookingConfirmation from './components/BookingConfirmation';
import { appointmentService, createPublicBooking } from '../../services/appointmentService';
import { supabase } from '../../lib/supabase';
import { useAvailableTimeSlots } from 'hooks';

const PublicPatientBookingScreen = () => {
  const [searchParams] = useSearchParams();
  const { psychologistId: routeParamId } = useParams();
  const navigate = useNavigate();

const {
  availableTimeSlots,
  loadingAvailableTimeSlots,
  errorAvailableTimeSlots,
  getAvailableTimeSlots, 
} = useAvailableTimeSlots();  
  
  // Support both route parameter and query parameter for backwards compatibility
  const psychologistId = routeParamId || searchParams?.get('psychologist_id');

  // Validate UUID format
  const isValidUUID = (id) => {
    if (!id || typeof id !== 'string') return false;
    // UUID v4 regex pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidPattern?.test(id);
  };

  // Form state
  const [fullName, setFullName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // UI state
  const [psychologist, setPsychologist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bookingComplete, setBookingComplete] = useState(false);
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const [errors, setErrors] = useState({});

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Create refs for scroll functionality
  const patientInfoRef = useRef(null);
  const dateSelectorRef = useRef(null);
  const timeSlotRef = useRef(null);

  // Load available time slots
  useEffect(() => {
    const loadAvailableTimeSlots = async () => {
      if (!selectedDate || !psychologistId || !psychologist) {
        return;
      }

      try {
        await getAvailableTimeSlots(selectedDate, psychologistId, psychologist?.work_lunch_interval);
      } catch (err) {
        console.error('Error loading available time slots:', err);
        setErrors('Erro ao carregar horários disponíveis');
      }
    };

    loadAvailableTimeSlots();
  
  }, [selectedDate, psychologistId, psychologist?.work_lunch_interval, getAvailableTimeSlots]);


  useEffect(() => {
    const agora = new Date(); // data/hora atual no fuso do navegador (Brasil)

    // Criamos a data de hoje com hora, minuto, segundo e milissegundo zerados
    const hojeMeiaNoiteLocal = new Date(
      agora.getFullYear(),   
      agora.getMonth(),      
      agora.getDate(),       
      0,                     
      0,                     
      0,                     
      0                      
    );

    setSelectedDate(hojeMeiaNoiteLocal);
  }, []);

  // Load psychologist info on mount
  useEffect(() => {
    if (!psychologistId) {
      setError('ID do psicólogo não fornecido na URL. Por favor, use o link de agendamento fornecido pelo profissional.');
      setLoading(false);
      return;
    }

    // Validate UUID format before making database call
    if (!isValidUUID(psychologistId)) {
      setError('ID do psicólogo inválido. Verifique se você está usando o link correto de agendamento fornecido pelo profissional.');
      setLoading(false);
      return;
    }

    loadPsychologistInfo();
  }, [psychologistId]);

  // 🔥 REAL-TIME: Setup subscription for appointment changes (separate effect for stability)
  useEffect(() => {
    if (!psychologistId) {
      return;
    }

    //console.log('🔌 Setting up real-time subscription for psychologist:', psychologistId);
    
    const channelName = `appointments-psych-${psychologistId}-${Math.floor(Math.random() * 1000000)}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'appointments',
        },
        (payload) => {
          //console.log('🔄 Real-time appointment change detected:', payload);
          
            // Reload available slots automatically
            if (selectedDate) {
              getAvailableTimeSlots(selectedDate, psychologistId, psychologist?.work_lunch_interval?.work_lunch_interval || null);
            }            
        }
      ).subscribe((status) => {
        //console.log('📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          //console.log('✅ Successfully subscribed to real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        }
      });

      // Carrega os slots iniciais (só quando selectedDate existe)
      if (selectedDate) {
        getAvailableTimeSlots(selectedDate, psychologistId, psychologist?.work_lunch_interval || null);
      }

    // Cleanup subscription when component unmounts or dependencies change
    return () => {
      //console.log('🔌 Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [psychologistId, selectedDate]); // Re-subscribe when date changes to update the filter logic

  
useEffect(() => {
  if (!psychologistId) {
    return;
  }

  //console.log('🔌 Setting up real-time subscription for psychologist:', psychologistId);
  
  const channelName = `temp-appointments-psych-${psychologistId}-${Math.floor(Math.random() * 1000000)}`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // Listen to INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'temp_public_patients',
      },
      (payload) => {
        //console.log('🔄 Real-time appointment change detected:', payload);
        
          // Reload available slots automatically
          if (selectedDate) {
            getAvailableTimeSlots(selectedDate, psychologistId, psychologist?.work_lunch_interval?.work_lunch_interval || null);
          }            
      }
    ).subscribe((status) => {
      //console.log('📡 Subscription status:', status);
      
      if (status === 'SUBSCRIBED') {
        //console.log('✅ Successfully subscribed to real-time updates');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ Channel subscription error');
      }
    });

    // Carrega os slots iniciais (só quando selectedDate existe)
    if (selectedDate) {
      getAvailableTimeSlots(selectedDate, psychologistId, psychologist?.work_lunch_interval?.work_lunch_interval || null);
    }

  // Cleanup subscription when component unmounts or dependencies change
  return () => {
    //console.log('🔌 Cleaning up real-time subscription');
    supabase.removeChannel(channel);
  };
}, [psychologistId, selectedDate]); // Re-subscribe when date changes to update the filter logic


  const loadPsychologistInfo = async () => {
    try {
      setLoading(true);
      const { data, error } = await appointmentService?.getPublicPsychologistInfo(psychologistId);
      
      if (error) {
        // Handle error properly - extract message from error object
        const errorMessage = error?.message || 'Erro ao carregar informações. Tente novamente.';
        setError(errorMessage);
        return;
      }
      
      if (!data) {
        setError('Psicólogo não encontrado ou página pública não habilitada');
        return;
      }
      setPsychologist(data);
      setError(''); // Clear any previous errors on success
    } catch (err) {
      console.error('Erro ao carregar informações do psicólogo:', err);
      const errorMessage = err?.message || 'Erro ao carregar informações. Tente novamente.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Format whatsapp number.
  const formatWhatsApp = (value) => {
    // Remove all non-digit characters except the leading '+'
    let numbers = value?.replace(/[^\d+]/g, '');
    
    // If it starts with +, preserve it
    const hasPlus = numbers?.startsWith('+');
    if (hasPlus) {
      numbers = numbers?.substring(1); // Remove + temporarily for formatting
    }
    
    // If less than or equal to 2 digits, just return as-is
    if (numbers?.length <= 2) {
      return hasPlus ? `+${numbers}` : numbers;
    }
    
    // Format based on number length
    let formatted;
    if (numbers?.length <= 7) {
      // Short format: (XX) XXXXX
      formatted = `(${numbers?.slice(0, 2)}) ${numbers?.slice(2)}`;
    } else if (numbers?.length <= 11) {
      // Brazilian format: (XX) XXXXX-XXXX
      formatted = `(${numbers?.slice(0, 2)}) ${numbers?.slice(2, 7)}-${numbers?.slice(7)}`;
    } else {
      // International format: Handle country codes
      // Example: +55 (21) 99999-9999 for Brazil
      const countryCodeLength = Math.min(3, numbers?.length - 11); // Max 3 digits for country code
      const countryCode = numbers?.slice(0, countryCodeLength);
      const restOfNumber = numbers?.slice(countryCodeLength);
      
      if (restOfNumber?.length <= 7) {
        formatted = `+${countryCode} (${restOfNumber?.slice(0, 2)}) ${restOfNumber?.slice(2)}`;
      } else if (restOfNumber?.length <= 11) {
        formatted = `+${countryCode} (${restOfNumber?.slice(0, 2)}) ${restOfNumber?.slice(2, 7)}-${restOfNumber?.slice(7)}`;
      } else {
        // Very long number, just add country code
        formatted = `+${countryCode} ${restOfNumber}`;
      }
      return formatted;
    }
    
    // Add back the + if it was present
    return hasPlus ? `+${formatted}` : formatted;
  };

  const handleWhatsAppChange = (e) => {
    let formatted = formatWhatsApp(e?.target?.value);
    setWhatsappNumber(formatted);
    if (validationErrors?.whatsappNumber) {
      setValidationErrors(prev => ({ ...prev, whatsappNumber: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!fullName?.trim()) {
      errors.fullName = 'Nome completo é obrigatório';
    } else if (fullName?.trim()?.split(' ')?.length < 2) {
      errors.fullName = 'Digite seu nome completo';
    }
    
    // Updated validation to accept international numbers
    let numbers = whatsappNumber?.replace(/\D/g, '');
    if (!whatsappNumber?.trim()) {
      errors.whatsappNumber = 'WhatsApp é obrigatório';
    } else if (numbers?.length < 10) {
      errors.whatsappNumber = 'WhatsApp inválido. Digite um número válido com código de país (ex: +55)';
    }
    
    if (!selectedDate) {
      errors.selectedDate = 'Selecione uma data';
    }
    
    if (!selectedTime) {
      errors.selectedTime = 'Selecione um horário';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // New function to scroll to first error field
  const scrollToFirstError = (errors) => {
    // Priority order: Patient Info → Date → Time
    if (errors?.fullName || errors?.whatsappNumber) {
      patientInfoRef?.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      // Focus on the specific input field
      setTimeout(() => {
        if (errors?.fullName) {
          document?.getElementById('fullName')?.focus();
        } else if (errors?.whatsappNumber) {
          document?.getElementById('whatsappNumber')?.focus();
        }
      }, 500);
    } else if (errors?.selectedDate) {
      dateSelectorRef?.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else if (errors?.selectedTime) {
      timeSlotRef?.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      // Scroll to the first field with error
      scrollToFirstError(validationErrors);
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      const { data, error } = await createPublicBooking({
        fullName: fullName?.trim(),
        whatsappNumber: whatsappNumber?.replace(/\D/g, ''),
        appointmentDate: selectedDate,
        startTime: selectedTime,
        psychologistId: psychologistId
      });
      
      if (error) throw error;
      
      setConfirmedAppointment(data);
      setBookingComplete(true);
    } catch (err) {
      console.error('Erro ao criar agendamento:', err);
      setError(err?.message || 'Erro ao criar agendamento. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error && !psychologist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card rounded-2xl shadow-xl border border-border p-8 text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Erro</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="primary">
            Voltar para página inicial
          </Button>
        </div>
      </div>
    );
  }

  if (bookingComplete) {
    return (
      <BookingConfirmation 
        appointment={confirmedAppointment}
        psychologist={psychologist}
        onClose={() => navigate('/')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Calendar" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">
              Agendamento Online
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Agende sua consulta
          </h1>
          <p className="text-xl text-muted-foreground">
            Preencha os dados abaixo para marcar seu atendimento
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
          {/* Psychologist Header */}
          {psychologist && (
            <div className="bg-gradient-to-r from-primary to-blue-700 p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                {psychologist?.profile_photo_url || psychologist?.clinic_logo_url ? (
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-white/20 flex-shrink-0">
                    <img 
                      src={psychologist?.profile_photo_url || psychologist?.clinic_logo_url}
                      alt={psychologist?.full_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={32} color="#FFFFFF" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold">{psychologist?.full_name}</h2>
                  {psychologist?.clinic_name && (
                    <p className="text-white/90">{psychologist?.clinic_name}</p>
                  )}
                </div>
              </div>
              {psychologist?.specialization && (
                <p className="text-white/90 mb-2">{psychologist?.specialization}</p>
              )}
              <div className="flex items-center gap-4 text-white/90">
                <div className="hidden flex items-center gap-2">
                  <Icon name="Clock" size={16} color="#FFFFFF" />
                  <span>{psychologist?.consultation_duration || 50} minutos</span>
                </div>
                {psychologist?.consultation_price && (
                  <div className="flex items-center gap-2">
                    <Icon name="DollarSign" size={16} color="#FFFFFF" />
                    <span>
                      {psychologist?.currency === 'BRL' ? 'R$' : '$'} {psychologist?.consultation_price}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            {/* Patient Info */}
            <div ref={patientInfoRef}>
              <PatientInfoForm
                fullName={fullName}
                setFullName={setFullName}
                whatsappNumber={whatsappNumber}
                handleWhatsAppChange={handleWhatsAppChange}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              />
            </div>

            {/* Date Selection */}
            <div ref={dateSelectorRef}>
              <DateSelector
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                validationErrors={validationErrors}
              />
            </div>

            {/* Time Slot Selection */}
            {selectedDate && (
              <div ref={timeSlotRef}>
                <TimeSlotGrid
                  key={availableTimeSlots.length}
                  availableTimeSlots={availableTimeSlots}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  loadingAvailableTimeSlots={loadingAvailableTimeSlots}
                  validationErrors={validationErrors}
                  psychologist={psychologist}
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            {selectedDate && selectedTime && (
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  iconName={submitting ? "Loader" : "Calendar"}
                  iconPosition="left"
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? 'Confirmando...' : 'Confirmar Agendamento'}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicPatientBookingScreen;